/**
 * Autore: Salvo de Luca
 * Azienda: Dialogicom s.r.l.s.
 * File: src/config/navigationConfig.ts
 * Versione: v5.9.280 - Menu header ristrutturato
 *
 * ============================================================
 * MENU HEADER NAVIGAZIONE v5.9.280
 * ============================================================
 *
 * Menu header con navigazione semplificata.
 * Ordine: Accettazione | Magazzino | Pagamenti | Elenchi | Amministrazione
 *
 * Comportamento responsive gestito da AdaptiveMenu:
 * - Desktop (>768px): Menu orizzontale con dropdown
 * - Mobile (<768px): Menu hamburger (sandwich)
 *
 * ============================================================
 */

import type { MenuItem } from '../components/ui-kit-dialogicom/layouts/AdaptiveMenu';
import { appToast } from '../lib/appDialog';
import {
  LayoutDashboard,
  Package,
  Users,
  Settings,
  FileText,
  UserPlus,
  Clock,
  Search,
  Zap,
  ScanLine,
  Warehouse,
  List,
  Smartphone,
  Cog,
  ClipboardCheck,
  FileCheck,
  Beaker,
  Trash2,
  FlaskConical,
  Archive,
  RefreshCw,
  MessageCircle
} from 'lucide-react';

/**
 * ============================================================
 * WORKFLOW PHASE ENUM
 * ============================================================
 */
export type WorkflowPhase = 'dashboard' | 'ingresso' | 'lavorazione' | 'commerciale' | 'other';

/**
 * ============================================================
 * OLD MENU - Lista piatta di tutte le pagine del menu precedente
 * ============================================================
 * Nota: struttura piatta perché AdaptiveMenu supporta max 3 livelli
 * (Amministrazione → Old Menu → voce)
 */
const oldMenuItems: MenuItem[] = [
  // === DASHBOARD ===
  {
    id: 'old-dashboard',
    label: 'Dashboard',
    icon: LayoutDashboard,
    onClick: () => { window.location.href = '/dashboard'; }
  },

  // === ACCETTAZIONE ===
  {
    id: 'old-accettazione-wizard',
    label: 'Accettazione - Wizard V2',
    icon: Package,
    onClick: () => { window.location.href = '/admin/accettazione-v2'; }
  },
  {
    id: 'old-accettazione-completa',
    label: 'Accettazione - Apparati Completa',
    icon: ClipboardCheck,
    onClick: () => { window.location.href = '/accettazione'; }
  },
  {
    id: 'old-accettazione-rapida',
    label: 'Accettazione - Apparati Rapida',
    icon: Zap,
    onClick: () => { window.location.href = '/admin/accettazione-rapida'; }
  },
  {
    id: 'old-clienti-completa',
    label: 'Nuovi Clienti - Completa',
    icon: UserPlus,
    onClick: () => { window.location.href = '/admin/profile-form'; }
  },
  {
    id: 'old-clienti-rapida',
    label: 'Nuovi Clienti - Rapida',
    icon: Zap,
    onClick: () => { window.location.href = '/admin/inserimento-cliente-rapido'; }
  },
  {
    id: 'old-accettazione-pending',
    label: 'Accettazione - In Pending',
    icon: Clock,
    onClick: () => { window.location.href = '/admin/pending'; }
  },

  // === MAGAZZINO ===
  {
    id: 'old-magazzino-dashboard',
    label: 'Magazzino - Dashboard Ricambi',
    icon: Warehouse,
    onClick: () => { window.location.href = '/magazzino'; }
  },
  {
    id: 'old-magazzino-nuovo-form',
    label: 'Magazzino - Nuovo da Form',
    icon: FileText,
    onClick: () => { window.location.href = '/magazzino?action=nuovo-articolo'; }
  },
  {
    id: 'old-magazzino-nuovo-ocr',
    label: 'Magazzino - Nuovo da OCR',
    icon: ScanLine,
    onClick: () => { window.location.href = '/magazzino?action=ocr-fattura'; }
  },
  {
    id: 'old-magazzino-ricerca',
    label: 'Magazzino - Ricerca',
    icon: Search,
    onClick: () => { window.location.href = '/magazzino'; }
  },

  // === ANALISI ===
  {
    id: 'old-analisi-completa',
    label: 'Analisi - Completa',
    icon: Search,
    onClick: () => { window.location.href = '/analisi-guasti-preventivi'; }
  },

  // === ELENCHI ===
  {
    id: 'old-clienti-tutti',
    label: 'Elenchi - Tutti i Clienti',
    icon: Users,
    onClick: () => { window.location.href = '/admin/users'; }
  },
  {
    id: 'old-clienti-da-confermare',
    label: 'Elenchi - Clienti da Confermare',
    icon: Clock,
    onClick: () => { window.location.href = '/admin/pending-customers'; }
  },
  {
    id: 'old-elenchi-gruppi',
    label: 'Elenchi - Lista Gruppi',
    icon: Users,
    onClick: () => { window.location.href = '/admin/groups'; }
  }
];

/**
 * ============================================================
 * MENU NAVIGAZIONE HEADER - Nuovo layout semplificato
 * ============================================================
 * Ordine: Accettazione | Magazzino | Pagamenti | Elenchi | Amministrazione
 */
const headerNavigationItems: MenuItem[] = [
  // ═══════════════════════════════════════════════════════════════
  // ACCETTAZIONE - porta alla Dashboard principale
  // ═══════════════════════════════════════════════════════════════
  {
    id: 'accettazione',
    label: 'Accettazione',
    icon: Package,
    onClick: () => { window.location.href = '/dashboard'; }
  },

  // ═══════════════════════════════════════════════════════════════
  // MAGAZZINO - porta alla Dashboard Ricambi
  // ═══════════════════════════════════════════════════════════════
  {
    id: 'magazzino',
    label: 'Magazzino',
    icon: Warehouse,
    onClick: () => { window.location.href = '/magazzino'; }
  },

  // PAGAMENTI: voce rimossa dalla top-bar (portava a una pagina "in costruzione").
  // La rotta /pagamenti resta, ma non è più raggiungibile dal menù.

  // ═══════════════════════════════════════════════════════════════
  // ELENCHI - uguale all'attuale
  // ═══════════════════════════════════════════════════════════════
  {
    id: 'elenchi',
    label: 'Elenchi',
    icon: List,
    children: [
      {
        id: 'elenchi-clienti',
        label: 'Lista Clienti',
        icon: Users,
        children: [
          {
            id: 'clienti-tutti',
            label: 'Tutti',
            icon: Users,
            onClick: () => { window.location.href = '/admin/users'; }
          },
          {
            id: 'clienti-da-confermare',
            label: 'Da confermare',
            icon: Clock,
            onClick: () => { window.location.href = '/admin/pending-customers'; }
          },
          {
            id: 'clienti-definitivi',
            label: 'Definitivi',
            icon: FileCheck,
            onClick: () => { window.location.href = '/admin/users?status=confirmed'; }
          }
        ]
      },
      {
        id: 'elenchi-gruppi',
        label: 'Lista Gruppi',
        icon: Users,
        // Gruppi e utenti d'officina sono materia del solo superadmin (e /admin/groups già
        // rifiuta gli altri: la voce mostrata all'admin portava a un "accesso negato").
        requiredRole: 'superadmin',
        onClick: () => { window.location.href = '/admin/groups'; }
      },
      {
        id: 'elenchi-checkin',
        label: 'Check-in',
        icon: ClipboardCheck,
        children: [
          {
            id: 'checkin-tutti',
            label: 'Tutti',
            icon: ClipboardCheck,
            onClick: () => { window.location.href = '/admin/users'; }
          },
          {
            id: 'checkin-da-confermare',
            label: 'Da confermare',
            icon: Clock,
            onClick: () => { window.location.href = '/admin/pending-customers'; }
          },
          {
            id: 'checkin-confermati',
            label: 'Confermati',
            icon: FileCheck,
            onClick: () => { window.location.href = '/admin/users?status=confirmed'; }
          }
        ]
      }
    ]
  },

  // ═══════════════════════════════════════════════════════════════
  // AMMINISTRAZIONE - solo voci funzionanti + Old Menu
  // ═══════════════════════════════════════════════════════════════
  {
    id: 'amministrazione',
    label: 'Amministrazione',
    icon: Settings,
    requiredRole: 'superadmin',
    adminVisible: true,
    children: [
      // ─── Sotto-gruppo: Apparati & Officina (visibile anche agli admin) ───
      {
        id: 'admin-grp-apparati',
        label: 'Apparati & Officina',
        icon: Package,
        adminVisible: true,
        children: [
          {
            id: 'admin-config-prezzi-apparati',
            label: 'Configurazione prezzi apparati',
            icon: Settings,
            adminVisible: true,
            onClick: () => { window.location.href = '/admin/apparati/admin-config'; }
          },
          {
            id: 'admin-setup-banco-prova',
            label: 'Setup banco prova iniettori',
            icon: Settings,
            adminVisible: true,
            onClick: () => { window.location.href = '/admin/apparati/setup-banco-prova'; }
          },
          {
            id: 'admin-archivio-dati-iniettori',
            label: 'Banca dati iniettori',
            icon: Archive,
            adminVisible: true,
            onClick: () => { window.location.href = '/admin/apparati/banca-dati'; }
          },
          {
            id: 'admin-notifiche-programmi-test',
            label: 'Notifiche programmi test',
            icon: Settings,
            adminVisible: true,
            onClick: () => { window.location.href = '/admin/apparati/programmi-test/notifiche'; }
          }
        ]
      },
      // ─── Sotto-gruppo: Utenti & Operatori ───
      {
        id: 'admin-grp-utenti',
        label: 'Utenti & Operatori',
        icon: Users,
        adminVisible: true,
        children: [
          {
            id: 'admin-utenti-officina',
            label: 'Gestione Utenti officina',
            icon: Users,
            adminVisible: false, // solo superadmin: gestione utenti /admin/users nascosta al gruppo admin
            onClick: () => { window.location.href = '/admin/users'; }
          },
          {
            id: 'admin-operatori-mobile',
            label: 'Gestione operatori smartphone',
            icon: Smartphone,
            adminVisible: true,
            onClick: () => {
              if (typeof (window as any).openMobileOperatorsModal === 'function') {
                (window as any).openMobileOperatorsModal();
              } else {
                appToast.error('❌ Funzione non disponibile.\nRicarica la pagina e riprova.');
              }
            }
          }
        ]
      },
      // ─── Sotto-gruppo: Sistema & Integrazioni ───
      {
        id: 'admin-grp-sistema',
        label: 'Sistema & Integrazioni',
        icon: Cog,
        // Deve restare visibile all'admin: il filtro e' ricorsivo e scarta
        // l'intero ramo prima di guardare i figli, quindi senza questo la voce
        // dentro non verrebbe mai valutata. Le altre voci del gruppo restano
        // riservate al superadmin perche' non dichiarano adminVisible.
        adminVisible: true,
        children: [
          {
            // Import dai magazzini rotanti COSPRO + azzeramento dati di test.
            // adminVisible resta false: cancella dati, e' materia da superadmin.
            id: 'admin-magazzino-sync',
            label: 'Magazzini rotanti — sincronizzazione',
            icon: Package,
            // Visibile a superadmin e admin. I comandi distruttivi (import e
            // azzeramento) restano comunque preclusi all'admin: li nasconde la
            // pagina e li rifiuta il backend con 403.
            adminVisible: true,
            onClick: () => { window.location.href = '/admin/magazzino-sync'; }
          },
          {
            id: 'admin-system-config',
            label: 'Impostazioni Sistema',
            icon: Settings,
            onClick: () => {
              if (typeof (window as any).openSystemConfigModal === 'function') {
                (window as any).openSystemConfigModal();
              } else {
                appToast.error('❌ Funzione non disponibile.\nRicarica la pagina e riprova.');
              }
            }
          },
          {
            id: 'admin-config-hub',
            label: 'Configuration Hub',
            icon: Cog,
            onClick: () => { window.location.href = '/admin/config-hub'; }
          },
          {
            id: 'admin-whatsapp-bot',
            label: 'WhatsApp Bot',
            icon: MessageCircle,
            onClick: () => {
              if (typeof (window as any).openWhatsAppBotModal === 'function') {
                (window as any).openWhatsAppBotModal();
              } else {
                appToast.error('❌ Funzione non disponibile.\nRicarica la pagina e riprova.');
              }
            }
          }
        ]
      },
      // ─── Sotto-gruppo: Log & Audit ───
      {
        id: 'admin-grp-log',
        label: 'Log & Audit',
        icon: Clock,
        children: [
          {
            id: 'admin-audit-operatori',
            label: 'Audit operatori (chi ha fatto cosa)',
            icon: Cog,
            onClick: () => { window.location.href = '/admin/audit-operatori'; }
          },
          {
            id: 'admin-workflow-log',
            label: 'Cronologia Workflow',
            icon: Clock,
            onClick: () => {
              if (typeof (window as any).openWorkflowLogModal === 'function') {
                (window as any).openWorkflowLogModal();
              } else {
                appToast.error('❌ Funzione non disponibile.\nRicarica la pagina e riprova.');
              }
            }
          }
        ]
      },
      // ─── Sotto-gruppo: Manutenzione & Sviluppo ───
      {
        id: 'admin-grp-dev',
        label: 'Manutenzione & Sviluppo',
        icon: FlaskConical,
        children: [
          {
            id: 'admin-dev-simulator',
            label: 'Simulatore Dati (DEV)',
            icon: Beaker,
            onClick: () => {
              if (typeof (window as any).toggleDevSimulator === 'function') {
                (window as any).toggleDevSimulator();
              } else {
                appToast.error('❌ Simulatore non disponibile.\nRicarica la pagina e riprova.');
              }
            }
          },
          {
            id: 'admin-dev-settings',
            label: 'Impostazioni Dev',
            icon: FlaskConical,
            onClick: () => {
              if (typeof (window as any).openDevSettings === 'function') {
                (window as any).openDevSettings();
              } else {
                appToast.error('❌ DevSettings non disponibile.\nRicarica la pagina e riprova.');
              }
            }
          },
          {
            id: 'admin-force-update',
            label: 'Aggiornamento Forzato',
            icon: RefreshCw,
            onClick: () => { window.location.href = '/admin/force-update'; }
          },
          {
            id: 'admin-data-cleanup',
            label: 'Elimina Dati',
            icon: Trash2,
            onClick: () => {
              if (typeof (window as any).openDataCleanupModal === 'function') {
                (window as any).openDataCleanupModal();
              } else {
                appToast.error('❌ Funzione non disponibile.\nRicarica la pagina e riprova.');
              }
            }
          },
          {
            id: 'admin-magazzino-old',
            label: 'Magazzino OLD',
            icon: Warehouse,
            onClick: () => { window.location.href = '/magazzino-old'; }
          }
        ]
      },
      // OLD MENU - livello 2 (i suoi oldMenuItems restano al livello 3, renderizzabili)
      {
        id: 'admin-old-menu',
        label: 'Old Menu',
        icon: Archive,
        children: oldMenuItems
      }
    ]
  }
];

// Tutti usano lo stesso menu di navigazione
export const ingressoMenuItems: MenuItem[] = headerNavigationItems;
export const lavorazioneMenuItems: MenuItem[] = headerNavigationItems;
export const commercialeMenuItems: MenuItem[] = headerNavigationItems;
export const dashboardMenuItems: MenuItem[] = headerNavigationItems;

/**
 * ============================================================
 * UTILITY: Determina la fase corrente dal path
 * ============================================================
 */
export const getCurrentPhase = (path?: string): WorkflowPhase => {
  const currentPath = path || window.location.pathname;

  // INGRESSO E ACCETTAZIONE
  if (
    currentPath === '/accettazione' ||
    currentPath.startsWith('/accettazione/') ||
    currentPath === '/admin/pending' ||
    currentPath === '/admin/pending-turbines' ||
    currentPath === '/admin/nuovo-inserimento'
  ) {
    return 'ingresso';
  }

  // LAVORAZIONE
  if (
    currentPath === '/analisi-guasti-preventivi' ||
    currentPath.startsWith('/analisi-guasti-preventivi/')
    // NOTE: /lavorazione non ancora implementata
  ) {
    return 'lavorazione';
  }

  // COMMERCIALE - non ancora implementato
  // if (currentPath.startsWith('/commerciale/')) {
  //   return 'commerciale';
  // }

  // DASHBOARD
  if (currentPath === '/dashboard') {
    return 'dashboard';
  }

  return 'other';
};

/**
 * ============================================================
 * UTILITY: Ottieni menu items per la fase corrente
 * ============================================================
 */
export const getContextualMenuItems = (phase?: WorkflowPhase, userRole?: string, userGroup?: string): MenuItem[] => {
  const currentPhase = phase || getCurrentPhase();

  let items: MenuItem[];
  switch (currentPhase) {
    case 'ingresso':
      items = ingressoMenuItems;
      break;
    case 'lavorazione':
      items = lavorazioneMenuItems;
      break;
    case 'commerciale':
      items = commercialeMenuItems;
      break;
    case 'dashboard':
      items = dashboardMenuItems;
      break;
    default:
      items = dashboardMenuItems;
  }

  const isSuper = userRole === 'superadmin' || userGroup === 'superadmin';
  const isAdmin = userRole === 'admin' || userGroup === 'admin';
  // superZone = true dentro un ramo con requiredRole 'superadmin'. Dentro la zona superadmin l'admin
  // vede solo le voci esplicitamente adminVisible; fuori vale il gating normale per ruolo.
  const filtra = (item: MenuItem, superZone: boolean): MenuItem | null => {
    const inSuperZone = superZone || item.requiredRole === 'superadmin';
    let vis: boolean;
    if (isSuper) vis = true;
    else if (inSuperZone) {
      // Valutata qui e non al caricamento del modulo: alcune voci dipendono da un
      // flag di configurazione che arriva dal server dopo il primo render.
      const av = typeof item.adminVisible === 'function' ? item.adminVisible() : item.adminVisible;
      vis = isAdmin && av === true;
    }
    else vis = !item.requiredRole || item.requiredRole === userRole;
    if (!vis) return null;
    if (item.children && item.children.length > 0) {
      const kids = item.children.map(c => filtra(c, inSuperZone)).filter(Boolean) as MenuItem[];
      return { ...item, children: kids };
    }
    return item;
  };

  if (userRole || userGroup) {
    items = items.map(i => filtra(i, false)).filter(Boolean) as MenuItem[];
  }

  return items;
};

/**
 * ============================================================
 * UTILITY: Determina l'active item ID
 * ============================================================
 */
export const getActiveNavigationId = (): string => {
  const path = window.location.pathname;
  const phase = getCurrentPhase(path);

  // In base alla fase, determina quale item è attivo
  switch (phase) {
    case 'ingresso':
      if (path === '/admin/pending' || path === '/admin/pending-turbines') return 'in-attesa';
      if (path === '/accettazione') return 'confermati';
      return 'tutti-apparati';

    case 'lavorazione':
      if (path === '/analisi-guasti-preventivi') return 'stato-lavorazione';
      return 'stato-lavorazione';

    case 'commerciale':
      // Non ancora implementato
      return '';

    case 'dashboard':
      return 'goto-ingresso';

    default:
      return '';
  }
};

/**
 * ============================================================
 * BADGE "BOZZA" - Per apparati non confermati
 * ============================================================
 *
 * Regole UI testuali:
 * - Se apparato NON confermato → mostra badge "Bozza"
 * - Disabilita azioni commerciali con tooltip "Disponibile dopo conferma"
 * - Non usare mai copy tecnico ("record temporaneo", "stato pending")
 * - Usare: "In attesa di conferma", "Bozza interna"
 */
export interface ApparatoState {
  id: string;
  stato: 'in_attesa_di_conferma' | 'in_accettazione' | 'in_lavorazione' | 'completato';
}

export const isBozza = (apparato: ApparatoState): boolean => {
  return apparato.stato === 'in_attesa_di_conferma';
};

export const getBozzaBadge = (apparato: ApparatoState): { label: string; color: string } | null => {
  if (isBozza(apparato)) {
    return {
      label: 'Bozza',
      color: 'bg-amber-500/20 text-amber-300 border-amber-500/30'
    };
  }
  return null;
};

export const getDisabledActionTooltip = (apparato: ApparatoState): string | null => {
  if (isBozza(apparato)) {
    return 'Disponibile dopo conferma';
  }
  return null;
};

/**
 * ============================================================
 * COMPATIBILITÀ: globalNavigationItems
 * ============================================================
 *
 * @deprecated Usare getContextualMenuItems() invece
 *
 * Questo export è mantenuto per compatibilità con pagine esistenti
 * che usano ancora il menu globale (es. UIDialogicomPage, TestPage).
 *
 * Per nuovi sviluppi, usare:
 * - getContextualMenuItems() per menu contestuale
 * - getCurrentPhase() per determinare la fase
 */
export const globalNavigationItems: MenuItem[] = [
  // Combina tutti i menu per compatibilità
  ...dashboardMenuItems,
  ...ingressoMenuItems,
  ...lavorazioneMenuItems,
  ...commercialeMenuItems
];
